import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Building2 } from 'lucide-react';
import { Layout } from '../components/Layout';
import { API_URL } from '../components/empresa360/emp360Shared';
import { companyLabel, empresaUrl } from '../components/client360/c360Shared';
import { EmpresaHeader } from '../components/empresa360/EmpresaHeader';
import { MonthSummary } from '../components/empresa360/MonthSummary';
import { FlowCard } from '../components/empresa360/FlowCard';
import { HoraDeMudarList } from '../components/empresa360/HoraDeMudarList';
import { ComerciaisRanking } from '../components/empresa360/ComerciaisRanking';

const EmpresasList = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/empresas/360`).then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div className="space-y-4" data-testid="emp360-list">
      <h1 className="text-3xl sm:text-4xl font-bold font-[Outfit] text-[#2D3E6F]">Empresas</h1>
      <p className="text-sm text-slate-500">Escolha uma empresa para ver o resumo: ganhos, perdidos, anulações e fidelizações a terminar.</p>
      {!rows ? <div className="spinner" /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {rows.map((r) => (
            <Link key={r.company} to={empresaUrl(r.company)} data-testid={`emp360-list-${r.company.toLowerCase()}`}
              className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:border-[#4DD4E8] transition-colors">
              <Building2 className="w-5 h-5 text-[#2D3E6F]" />
              <span className="flex-1 font-semibold">{companyLabel(r.company.toLowerCase())}</span>
              <span className="text-sm text-slate-500">{r.total} contr.</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const EmpresaView = ({ company }) => {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!company) return;
    setError('');
    axios.get(`${API_URL}/empresas/${encodeURIComponent(company)}/360`, { params: { month: month || undefined } })
      .then((r) => { setData(r.data); if (!month) setMonth(r.data.mes); })
      .catch((e) => setError((typeof e.response?.data?.detail === 'string' && e.response.data.detail) || 'Erro ao carregar a empresa'));
  }, [company, month]);
  useEffect(() => { load(); }, [load]);

  let body;
  if (error) body = <div className="space-y-6"><div className="p-4 rounded-xl bg-rose-50 text-rose-800 text-sm" data-testid="emp360-error">{error}</div><EmpresasList /></div>;
  else if (!data) body = <div className="flex justify-center py-20"><div className="spinner" /></div>;
  else {
    const m = month || data.mes;
    body = (
      <div className="space-y-5" data-testid="emp360-page">
        <EmpresaHeader d={data} month={m} setMonth={setMonth} />
        <div className="grid gap-5 lg:grid-cols-2">
          <MonthSummary d={data} month={data.mes} />
          <ComerciaisRanking rows={data.comerciais} d={data} month={data.mes} />
        </div>
        <FlowCard ganhos={data.ganhos} perdidos={data.perdidos} nome={companyLabel(data.empresa.company)} />
        <HoraDeMudarList rows={data.hora_de_mudar} onChanged={load} />
      </div>
    );
  }
  return body;
};

export default function Empresa360Page() {
  const { company } = useParams();
  return <Layout>{company ? <EmpresaView key={company} company={company} /> : <EmpresasList />}</Layout>;
}
